import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Loader2, GitBranch, Terminal, Camera, HeartPulse, Diff as DiffIcon,
  Trash2, Play, FileText, AlertTriangle, CheckCircle2, Copy,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = API + '/api/dev-console/phase2';

// ─── DIFF PREVIEW ─────────────────────────────────────────────────────────
function DiffTab({ sessionId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchDiff = useCallback(async () => {
    if (!sessionId) { setError('No active session — start a chat first'); return; }
    setLoading(true); setError(null);
    try {
      const r = await axios.post(BASE + '/diff-preview', { session_id: sessionId });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load diff');
    } finally { setLoading(false); }
  }, [sessionId]);

  useEffect(() => { fetchDiff(); }, [fetchDiff]);

  return (
    <div className="space-y-3" data-testid="phase2-diff-tab">
      <div className="flex items-center justify-between">
        <div className="text-xs text-zinc-400">
          {data ? (
            <>
              <strong className="text-zinc-100">{data.summary.files_changed}</strong> files ·
              <span className="text-emerald-400 ml-1">+{data.summary.insertions}</span>
              <span className="text-rose-400 ml-1">-{data.summary.deletions}</span>
            </>
          ) : '—'}
        </div>
        <Button size="sm" variant="outline" onClick={fetchDiff} disabled={loading} data-testid="phase2-diff-refresh">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refresh'}
        </Button>
      </div>
      {error && <div className="text-xs text-rose-400 bg-rose-950/40 border border-rose-800 rounded p-2">{error}</div>}
      {loading && !data && <div className="text-xs text-zinc-500 py-8 text-center">Loading diff…</div>}
      {data?.diffs?.length === 0 && (
        <div className="text-xs text-zinc-500 py-8 text-center">No staged changes yet — ask VibeKonnect to make an edit.</div>
      )}
      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        {data?.diffs?.map((d, i) => (
          <details key={i} className="border border-zinc-800 rounded bg-zinc-950 group" open={i === 0}>
            <summary className="cursor-pointer px-3 py-2 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 min-w-0">
                <Badge variant="outline" className={
                  'text-[9px] shrink-0 ' +
                  (d.kind === 'created' ? 'border-emerald-700 text-emerald-400' :
                   d.kind === 'deleted' ? 'border-rose-700 text-rose-400' :
                   'border-amber-700 text-amber-400')
                }>{d.kind}</Badge>
                <code className="font-mono text-zinc-300 truncate">{d.path}</code>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-2">
                <span className="text-emerald-400">+{d.insertions}</span>
                <span className="text-rose-400">-{d.deletions}</span>
              </div>
            </summary>
            <pre className="p-3 pt-0 text-[10.5px] leading-relaxed font-mono overflow-x-auto whitespace-pre bg-zinc-950 text-zinc-300 border-t border-zinc-800">
{(d.unified_diff || '(binary or empty)').split('\n').map((line, idx) => (
  <div key={idx} className={
    line.startsWith('+++') || line.startsWith('---') ? 'text-zinc-500' :
    line.startsWith('@@') ? 'text-cyan-400' :
    line.startsWith('+') ? 'text-emerald-400 bg-emerald-950/20' :
    line.startsWith('-') ? 'text-rose-400 bg-rose-950/20' :
    'text-zinc-500'
  }>{line || ' '}</div>
))}
            </pre>
          </details>
        ))}
      </div>
    </div>
  );
}

// ─── SANDBOX ──────────────────────────────────────────────────────────────
function SandboxTab() {
  const [lang, setLang] = useState('python');
  const [code, setCode] = useState('# Hello from the VibeKonnect sandbox\nimport sys, platform\nprint("python:", platform.python_version())\nprint("cwd:", __import__("os").getcwd())');
  const [stdin, setStdin] = useState('');
  const [timeout, setTimeoutSec] = useState(10);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true); setResult(null);
    try {
      const r = await axios.post(BASE + '/sandbox/run', {
        code, lang, timeout: parseInt(timeout), stdin: stdin || null,
      });
      setResult(r.data);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || String(e) });
    } finally { setRunning(false); }
  };

  return (
    <div className="space-y-3" data-testid="phase2-sandbox-tab">
      <div className="flex gap-2 items-center flex-wrap">
        <select value={lang} onChange={(e) => setLang(e.target.value)} className="h-8 text-xs bg-zinc-900 border border-zinc-700 rounded px-2 text-zinc-200" data-testid="phase2-sandbox-lang">
          <option value="python">python</option>
          <option value="node">node</option>
          <option value="bash">bash</option>
          <option value="sh">sh</option>
        </select>
        <label className="text-xs text-zinc-400 flex items-center gap-1">
          Timeout:
          <Input type="number" min="1" max="30" value={timeout} onChange={(e) => setTimeoutSec(e.target.value)} className="h-8 w-16 text-xs" data-testid="phase2-sandbox-timeout" />
          s
        </label>
        <Button size="sm" onClick={run} disabled={running} className="ml-auto bg-emerald-600 hover:bg-emerald-500" data-testid="phase2-sandbox-run">
          {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
          Run
        </Button>
      </div>
      <Textarea
        value={code}
        onChange={(e) => setCode(e.target.value)}
        rows={8}
        className="font-mono text-[11px] bg-zinc-950 border-zinc-700 text-zinc-200"
        placeholder="Code to run…"
        data-testid="phase2-sandbox-code"
      />
      <details className="text-xs">
        <summary className="cursor-pointer text-zinc-400">stdin (optional)</summary>
        <Textarea value={stdin} onChange={(e) => setStdin(e.target.value)} rows={2} className="font-mono text-[11px] mt-1 bg-zinc-950 border-zinc-700 text-zinc-200" data-testid="phase2-sandbox-stdin" />
      </details>
      {result && (
        <div className="space-y-2" data-testid="phase2-sandbox-result">
          {result.error ? (
            <div className="text-xs text-rose-400 bg-rose-950/40 border border-rose-800 rounded p-2">{result.error}</div>
          ) : (
            <>
              <div className="flex items-center gap-3 text-xs">
                <Badge variant="outline" className={result.exit_code === 0 ? 'border-emerald-700 text-emerald-400' : 'border-rose-700 text-rose-400'}>
                  exit {result.exit_code}
                </Badge>
                <span className="text-zinc-400">{result.duration_ms}ms</span>
                {result.timed_out && <Badge variant="outline" className="border-amber-700 text-amber-400">timed out</Badge>}
                {result.truncated && <Badge variant="outline" className="border-amber-700 text-amber-400">truncated</Badge>}
              </div>
              {result.stdout && (
                <div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wide mb-1">stdout</div>
                  <pre className="p-2 text-[11px] font-mono bg-zinc-950 border border-zinc-800 rounded max-h-64 overflow-auto text-emerald-300">{result.stdout}</pre>
                </div>
              )}
              {result.stderr && (
                <div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wide mb-1">stderr</div>
                  <pre className="p-2 text-[11px] font-mono bg-zinc-950 border border-zinc-800 rounded max-h-40 overflow-auto text-rose-300">{result.stderr}</pre>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── THREADS (Chat Branching) ─────────────────────────────────────────────
function ThreadsTab({ sessionId, onLoadThread }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [forkAt, setForkAt] = useState('');
  const [forkName, setForkName] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(BASE + '/threads');
      setThreads(r.data.threads || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load threads');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const fork = async () => {
    if (!sessionId) { toast.error('No active session'); return; }
    try {
      const body = { session_id: sessionId };
      if (forkAt !== '') body.fork_from_message_index = parseInt(forkAt);
      if (forkName.trim()) body.name = forkName.trim();
      await axios.post(BASE + '/threads/fork', body);
      toast.success('Thread forked');
      setForkAt(''); setForkName('');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fork failed');
    }
  };

  const load = async (id) => {
    try {
      const r = await axios.get(BASE + '/threads/' + id);
      onLoadThread?.(r.data);
      toast.success('Thread loaded — history is now visible in the main chat.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Load failed');
    }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete thread "${name}"? This is irreversible.`)) return;
    try {
      await axios.delete(BASE + '/threads/' + id);
      toast.success('Deleted');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-3" data-testid="phase2-threads-tab">
      <div className="border border-zinc-800 rounded p-3 bg-zinc-950/60">
        <div className="text-xs font-medium text-zinc-200 mb-2 flex items-center gap-1.5">
          <GitBranch className="h-3.5 w-3.5" /> Fork current chat
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input placeholder="Name (optional)" value={forkName} onChange={(e) => setForkName(e.target.value)} className="h-8 text-xs" data-testid="phase2-fork-name" />
          <Input placeholder="From message # (optional)" value={forkAt} onChange={(e) => setForkAt(e.target.value.replace(/[^0-9]/g, ''))} className="h-8 text-xs" data-testid="phase2-fork-index" />
          <Button size="sm" onClick={fork} disabled={!sessionId} data-testid="phase2-fork-btn">Fork</Button>
        </div>
        <div className="text-[10px] text-zinc-500 mt-2">Leave &quot;message #&quot; empty to clone the entire session. Set it to truncate history at that message (0-based).</div>
      </div>
      <div className="flex items-center justify-between">
        <div className="text-xs text-zinc-400">{threads.length} saved thread(s)</div>
        <Button size="sm" variant="outline" onClick={refresh} disabled={loading} data-testid="phase2-threads-refresh">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refresh'}
        </Button>
      </div>
      <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
        {threads.map((t) => (
          <div key={t.id} className="flex items-center justify-between p-2 border border-zinc-800 rounded bg-zinc-950/40 text-xs">
            <div className="min-w-0 flex-1">
              <div className="text-zinc-200 truncate">{t.name}</div>
              <div className="text-[10px] text-zinc-500 truncate">
                {t.parent_thread_id ? `fork of ${t.parent_thread_id.slice(0,8)}… @ msg ${t.fork_from_message_index ?? 'tip'}` : 'root thread'}
                {' · '}
                {new Date(t.updated_at).toLocaleString()}
              </div>
            </div>
            <div className="flex gap-1 ml-2 shrink-0">
              <Button size="sm" variant="ghost" onClick={() => load(t.id)} className="h-6 px-2 text-[10px]" data-testid={`phase2-thread-load-${t.id.slice(0,6)}`}>Load</Button>
              <Button size="sm" variant="ghost" onClick={() => del(t.id, t.name)} className="h-6 px-2 text-[10px] text-rose-400 hover:text-rose-300"><Trash2 className="h-3 w-3" /></Button>
            </div>
          </div>
        ))}
        {threads.length === 0 && !loading && (
          <div className="text-xs text-zinc-500 py-6 text-center">No forks yet — use the panel above to save the first one.</div>
        )}
      </div>
    </div>
  );
}

// ─── SCREENSHOT ANALYSIS ──────────────────────────────────────────────────
function ScreenshotTab() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [context, setContext] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const pickFile = (f) => {
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error('Screenshot must be under 15MB'); return; }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
    setResult(null);
  };

  const analyze = async () => {
    if (!file) { toast.error('Attach a screenshot first'); return; }
    setAnalyzing(true); setResult(null);
    try {
      const b64 = preview.split(',')[1];
      const r = await axios.post(BASE + '/screenshot/analyze', {
        image_base64: b64,
        media_type: file.type || 'image/png',
        prompt: prompt.trim() || null,
        target_area_hint: context.trim() || null,
      }, { timeout: 60000 });
      setResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Vision analysis failed');
    } finally { setAnalyzing(false); }
  };

  const copyPrompt = (change) => {
    const text = `In ${change.file_hint || 'the codebase'}: ${change.description}`;
    navigator.clipboard.writeText(text);
    toast.success('Copied — paste into the VibeKonnect chat.');
  };

  return (
    <div className="space-y-3" data-testid="phase2-screenshot-tab">
      <div className="border-2 border-dashed border-zinc-700 rounded p-4 text-center bg-zinc-950/60">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={(e) => pickFile(e.target.files?.[0])}
          className="hidden"
          data-testid="phase2-screenshot-input"
        />
        {preview ? (
          <div>
            <img src={preview} alt="preview" className="max-h-52 mx-auto rounded border border-zinc-700" />
            <div className="text-[10px] text-zinc-500 mt-1">{file?.name} · {(file?.size / 1024).toFixed(0)}KB</div>
          </div>
        ) : (
          <div>
            <Camera className="h-6 w-6 text-zinc-500 mx-auto mb-1" />
            <div className="text-xs text-zinc-400">Attach a UI screenshot (PNG/JPEG/WebP, ≤15MB)</div>
          </div>
        )}
        <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} className="mt-2" data-testid="phase2-screenshot-pick">
          {preview ? 'Replace' : 'Choose file'}
        </Button>
      </div>
      <Input
        placeholder="Where is this screenshot from? e.g. 'ContentQueuePage draft card' (optional)"
        value={context}
        onChange={(e) => setContext(e.target.value)}
        className="text-xs"
        data-testid="phase2-screenshot-context"
      />
      <Textarea
        placeholder="What do you want to change? e.g. 'move the primary CTA above the fold' (optional)"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={2}
        className="text-xs"
        data-testid="phase2-screenshot-prompt"
      />
      <Button onClick={analyze} disabled={!file || analyzing} className="w-full bg-emerald-600 hover:bg-emerald-500" data-testid="phase2-screenshot-analyze">
        {analyzing ? <><Loader2 className="h-3 w-3 animate-spin mr-1.5" /> Kaharian Gateway is analyzing…</> : <><Camera className="h-3 w-3 mr-1.5" /> Analyze via Kaharian Gateway</>}
      </Button>
      {result && (
        <div className="space-y-2 border border-zinc-800 rounded p-3 bg-zinc-950/60 text-xs" data-testid="phase2-screenshot-result">
          <div className="flex items-center justify-between">
            <div className="font-medium text-zinc-200">Analysis</div>
            <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px]">
              {result._model_used || 'kaharian'} · confidence {Math.round((result.confidence || 0) * 100)}%
            </Badge>
          </div>
          {result.observations?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Observations</div>
              <ul className="list-disc list-inside space-y-0.5 text-zinc-300">
                {result.observations.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </div>
          )}
          {result.detected_issues?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Issues</div>
              <div className="space-y-1">
                {result.detected_issues.map((iss, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Badge variant="outline" className={
                      'text-[9px] shrink-0 mt-0.5 ' +
                      (iss.severity === 'high' ? 'border-rose-700 text-rose-400' :
                       iss.severity === 'medium' ? 'border-amber-700 text-amber-400' :
                       'border-zinc-700 text-zinc-400')
                    }>{iss.severity}</Badge>
                    <div>
                      <div className="text-zinc-200">{iss.issue}</div>
                      {iss.location_hint && <div className="text-[10px] text-zinc-500">{iss.location_hint}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.suggested_changes?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Suggested changes</div>
              <div className="space-y-1.5">
                {result.suggested_changes.sort((a, b) => (a.priority || 99) - (b.priority || 99)).map((s, i) => (
                  <div key={i} className="border border-zinc-800 rounded p-2 bg-zinc-900/50">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <code className="text-[10px] text-cyan-400">{s.file_hint || 'unknown'}</code>
                      <Button size="sm" variant="ghost" onClick={() => copyPrompt(s)} className="h-5 px-1.5 text-[9px] text-emerald-400 hover:text-emerald-300" data-testid={`phase2-screenshot-copy-${i}`}>
                        <Copy className="h-2.5 w-2.5 mr-0.5" /> Copy as prompt
                      </Button>
                    </div>
                    <div className="text-zinc-200">{s.description}</div>
                    <div className="text-[10px] text-zinc-500 mt-1">{s.change_type} · priority {s.priority || '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── HEALTH DIGEST ────────────────────────────────────────────────────────
function HealthTab() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(BASE + '/health-digest');
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load health digest');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="phase2-health-tab">
      <div className="flex items-center justify-between">
        <div className="text-[10px] text-zinc-500">
          {data ? `Generated ${new Date(data.generated_at).toLocaleTimeString()}` : '—'}
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="phase2-health-refresh">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refresh'}
        </Button>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <div className="border border-zinc-800 rounded p-2 bg-zinc-950/60">
              <div className="text-[10px] uppercase text-zinc-500">Files</div>
              <div className="text-lg font-semibold text-zinc-100">{data.totals.files.toLocaleString()}</div>
            </div>
            <div className="border border-zinc-800 rounded p-2 bg-zinc-950/60">
              <div className="text-[10px] uppercase text-zinc-500">Lines</div>
              <div className="text-lg font-semibold text-zinc-100">{data.totals.loc.toLocaleString()}</div>
            </div>
            <div className="border border-zinc-800 rounded p-2 bg-zinc-950/60">
              <div className="text-[10px] uppercase text-zinc-500">Size</div>
              <div className="text-lg font-semibold text-zinc-100">{(data.totals.bytes / 1024 / 1024).toFixed(1)}MB</div>
            </div>
          </div>

          <details className="border border-zinc-800 rounded bg-zinc-950/60" open>
            <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-zinc-200">By language</summary>
            <div className="px-3 pb-2 space-y-1 text-xs">
              {Object.entries(data.by_language).sort((a, b) => b[1].loc - a[1].loc).map(([lang, s]) => (
                <div key={lang} className="flex justify-between text-zinc-300">
                  <span>{lang}</span>
                  <span className="text-zinc-500">{s.files} files · {s.loc.toLocaleString()} LOC</span>
                </div>
              ))}
            </div>
          </details>

          <details className="border border-zinc-800 rounded bg-zinc-950/60">
            <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-zinc-200">
              Largest files (top 15)
            </summary>
            <div className="px-3 pb-2 space-y-1 text-[11px] font-mono">
              {data.top_15_largest_files.map((f, i) => (
                <div key={i} className="flex justify-between text-zinc-300">
                  <span className="truncate">{f.path}</span>
                  <span className={'ml-2 shrink-0 ' + (f.loc > 1500 ? 'text-rose-400' : f.loc > 800 ? 'text-amber-400' : 'text-zinc-500')}>
                    {f.loc.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </details>

          <details className="border border-zinc-800 rounded bg-zinc-950/60">
            <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-zinc-200 flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
              TODO / FIXME markers ({data.todo_markers.count})
            </summary>
            <div className="px-3 pb-2 space-y-1 text-[11px] font-mono max-h-48 overflow-auto">
              {data.todo_markers.sample.map((t, i) => (
                <div key={i} className="text-zinc-400">
                  <Badge variant="outline" className="text-[9px] mr-1 border-amber-700 text-amber-400">{t.marker}</Badge>
                  <span className="text-cyan-400">{t.path}:{t.line}</span>
                  <span className="text-zinc-500"> — {t.snippet}</span>
                </div>
              ))}
              {data.todo_markers.count > data.todo_markers.sample.length && (
                <div className="text-[10px] text-zinc-500 italic">…and {data.todo_markers.count - data.todo_markers.sample.length} more</div>
              )}
            </div>
          </details>

          <details className="border border-zinc-800 rounded bg-zinc-950/60">
            <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-zinc-200 flex items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-cyan-400" />
              Dead-code hints ({data.dead_code_hints.status === 'ok' ? data.dead_code_hints.unused_count : data.dead_code_hints.status})
            </summary>
            <div className="px-3 pb-2 space-y-1 text-[11px] font-mono">
              {data.dead_code_hints.status === 'ok' && data.dead_code_hints.sample?.map((h, i) => (
                <div key={i} className="text-zinc-400">
                  <Badge variant="outline" className="text-[9px] mr-1 border-zinc-700 text-zinc-500">{h.code}</Badge>
                  <span className="text-cyan-400">{h.file}:{h.line}</span>
                  <span className="text-zinc-500"> — {h.message}</span>
                </div>
              ))}
              {data.dead_code_hints.status !== 'ok' && (
                <div className="text-[10px] text-zinc-500 italic">Analyzer unavailable: {data.dead_code_hints.status}</div>
              )}
            </div>
          </details>

          <details className="border border-zinc-800 rounded bg-zinc-950/60">
            <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-zinc-200 flex items-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
              Dependency snapshot
            </summary>
            <div className="px-3 pb-2 space-y-1 text-xs">
              {Object.entries(data.dependency_freshness).map(([stack, s]) => (
                <div key={stack} className="text-zinc-300">
                  <strong className="text-zinc-100 capitalize">{stack}</strong> ({s.manifest}):
                  {s.dependencies !== undefined && <> {s.dependencies} deps, {s.devDependencies} devDeps</>}
                  {s.pinned_packages !== undefined && <> {s.pinned_packages} pinned packages</>}
                  <span className="text-zinc-500 text-[10px] ml-1">· updated {new Date(s.manifest_mtime).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </details>
        </>
      )}
    </div>
  );
}

// ─── MAIN PANEL ───────────────────────────────────────────────────────────
export function VibeKonnectPhase2Panel({ open, onOpenChange, sessionId, onLoadThread }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="phase2-panel">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 flex items-center gap-2">
            <span className="text-emerald-400">VibeKonnect</span> Phase-2
            <Badge variant="outline" className="text-[10px] border-emerald-700 text-emerald-400">v3.3</Badge>
          </DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="diff" className="w-full">
          <TabsList className="bg-zinc-950 border border-zinc-800">
            <TabsTrigger value="diff" className="text-xs data-[state=active]:bg-zinc-800" data-testid="phase2-tab-diff">
              <DiffIcon className="h-3 w-3 mr-1" /> Diff
            </TabsTrigger>
            <TabsTrigger value="sandbox" className="text-xs data-[state=active]:bg-zinc-800" data-testid="phase2-tab-sandbox">
              <Terminal className="h-3 w-3 mr-1" /> Sandbox
            </TabsTrigger>
            <TabsTrigger value="threads" className="text-xs data-[state=active]:bg-zinc-800" data-testid="phase2-tab-threads">
              <GitBranch className="h-3 w-3 mr-1" /> Threads
            </TabsTrigger>
            <TabsTrigger value="screenshot" className="text-xs data-[state=active]:bg-zinc-800" data-testid="phase2-tab-screenshot">
              <Camera className="h-3 w-3 mr-1" /> Screenshot
            </TabsTrigger>
            <TabsTrigger value="health" className="text-xs data-[state=active]:bg-zinc-800" data-testid="phase2-tab-health">
              <HeartPulse className="h-3 w-3 mr-1" /> Health
            </TabsTrigger>
          </TabsList>
          <TabsContent value="diff" className="mt-3"><DiffTab sessionId={sessionId} /></TabsContent>
          <TabsContent value="sandbox" className="mt-3"><SandboxTab /></TabsContent>
          <TabsContent value="threads" className="mt-3"><ThreadsTab sessionId={sessionId} onLoadThread={onLoadThread} /></TabsContent>
          <TabsContent value="screenshot" className="mt-3"><ScreenshotTab /></TabsContent>
          <TabsContent value="health" className="mt-3"><HealthTab /></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
